import { Error as ErrorToast, Info, Success, Warning } from '@/handler/toast'
import { webSocketService } from '@/services/webSocketService'
import { emitBackgroundReportCompleted } from '@/utils/backgroundReportBus'
import http from '@/config/http'

class BackgroundTaskQueue {
  constructor(concurrentLimit = 1) {
    this.queue = []
    this.running = 0
    this.concurrentLimit = concurrentLimit
    this.listeners = new Set()
    this.jobs = []
    this.lastId = 0
    this.maxJobs = 80
    this.wsInitialized = false
  }

  /**
   * Inicializar WebSocket para recibir notificaciones de jobs completados
   */
  initializeWebSocket(userId) {
    console.log('📞 initializeWebSocket llamado con userId:', userId, 'wsInitialized:', this.wsInitialized)

    if (this.wsInitialized) {
      console.log('⚠️ WebSocket ya inicializado, saltando...')
      return
    }

    console.log('🔌 Conectando WebSocket con userId:', userId)

    // Conectar al WebSocket
    webSocketService.connect(userId)

    // Escuchar eventos de job completado
    webSocketService.on('job-completed', (data) => {
      this.handleJobCompleted(data)
    })

    // Escuchar eventos de conexión
    webSocketService.on('connected', () => {
      console.log('✅ Cola de tareas conectada a WebSocket')
    })

    webSocketService.on('disconnected', () => {
      console.log('⚠️ Cola de tareas desconectada de WebSocket')
    })

    this.wsInitialized = true
  }

  /**
   * Manejar notificación de job completado desde WebSocket
   */
  async handleJobCompleted(data) {
    console.log('📨 Notificación de job completado recibida:', data)

    const { jobId, label, status, result, error } = data

    const trackedJob = this.jobs.find((x) => x.id === jobId || x.label === label)

    if (trackedJob) {
      trackedJob.status = status === 'success' ? 'success' : 'error'
      trackedJob.finishedAt = Date.now()

      if (status === 'success') {
        trackedJob.result = result
        
        // 🎯 SI HAY COMPLETION CONFIGURADO, OBTENER DATOS Y EMITIR EVENTO AUTOMÁTICAMENTE
        if (trackedJob.completion) {
          console.log('🔄 Job con completion detectado, obteniendo datos...')
          try {
            // Determinar el endpoint según el tipo de reporte
            const resultEndpoint = trackedJob.completion.resultEndpoint || '/lst_evolucion_feedlot/result'
            const url = `${resultEndpoint}?jobId=${jobId}`
            console.log('📡 Llamando a:', url)
            
            const response = await http.get(url)
            console.log('📦 Datos obtenidos:', response.data)
            
            // Extraer datos según la clave esperada (evolucion_feedlot, lst_analisis_consumo, etc.)
            const dataKey = trackedJob.completion.dataKey || 'evolucion_feedlot'
            const reportData = response.data?.[dataKey]
            
            if (reportData) {
              // Emitir evento para que BackgroundReportHost genere el archivo
              console.log('🔔 Emitiendo evento de reporte completado automáticamente')
              const responseData = {}
              responseData[dataKey] = reportData
              
              emitBackgroundReportCompleted({
                completion: trackedJob.completion,
                responseData: responseData,
                queueJobId: jobId
              })
            }
          } catch (err) {
            console.error('❌ Error al obtener resultado del job:', err)
            ErrorToast.show(`Error al obtener resultado: ${err.message}`)
          }
        }
        
        Success.show(`✅ ${label || 'Tarea'} completada correctamente`)
      } else {
        trackedJob.errorMessage = error || 'Error en tarea'
        ErrorToast.show(`❌ ${error || 'Error en tarea'}`)
      }

      this.notify()
    }
  }

  subscribe(listener) {
    if (typeof listener !== 'function') return () => {}
    this.listeners.add(listener)
    listener(this.getState())
    return () => {
      this.listeners.delete(listener)
    }
  }

  getState() {
    return {
      concurrentLimit: this.concurrentLimit,
      running: this.running,
      queued: this.queue.length,
      jobs: [...this.jobs],
    }
  }

  notify() {
    const snapshot = this.getState()
    this.listeners.forEach((listener) => {
      try {
        listener(snapshot)
      } catch (_) {
        // Ignora errores de listeners para no afectar la cola.
      }
    })
  }

  createTrackedJob(label, status = 'running') {
    const jobId = ++this.lastId
    const trackedJob = {
      id: jobId,
      label: label || 'Request HTTP',
      status,
      queuedAt: Date.now(),
      startedAt: status === 'running' ? Date.now() : null,
      finishedAt: null,
      errorMessage: null,
    }

    this.jobs.unshift(trackedJob)
    if (this.jobs.length > this.maxJobs) {
      this.jobs.length = this.maxJobs
    }

    this.notify()
    return trackedJob
  }

  trackHttpStart({ label } = {}) {
    const trackedJob = this.createTrackedJob(label || 'Request HTTP', 'running')
    return trackedJob.id
  }

  trackHttpSuccess(jobId) {
    const trackedJob = this.jobs.find((x) => x.id === jobId)
    if (!trackedJob) return
    trackedJob.status = 'success'
    trackedJob.finishedAt = Date.now()
    this.notify()
  }

  trackHttpError(jobId, errorMessage) {
    const trackedJob = this.jobs.find((x) => x.id === jobId)
    if (!trackedJob) return
    trackedJob.status = 'error'
    trackedJob.finishedAt = Date.now()
    trackedJob.errorMessage = errorMessage || 'Error HTTP'
    this.notify()
  }

  /**
   * Trackear un job de larga duración con un jobId personalizado (UUID)
   * @param {string} jobId - UUID del job (desde backend)
   * @param {string} label - Etiqueta descriptiva
   * @param {object} completion - Configuración de completion (opcional)
   * @returns {string} jobId
   */
  trackLongJob(jobId, label, completion = null) {
    const trackedJob = {
      id: jobId, // Usar el UUID del backend
      label: label || 'Reporte en segundo plano',
      status: 'running',
      queuedAt: Date.now(),
      startedAt: Date.now(),
      finishedAt: null,
      errorMessage: null,
      completion: completion // Guardar configuración de completion
    }

    this.jobs.unshift(trackedJob)
    if (this.jobs.length > this.maxJobs) {
      this.jobs.length = this.maxJobs
    }

    this.notify()
    return jobId
  }

  enqueue({
    label,
    run,
    queuedMessage,
    startedMessage,
    successMessage,
    errorMessage,
    onSuccess,
    onError,
    onFinally,
  }) {
    if (typeof run !== 'function') {
      throw new Error('BackgroundTaskQueue: run debe ser una funcion')
    }

    const jobLabel = label || 'Tarea en segundo plano'
    const jobId = ++this.lastId

    this.createTrackedJob(jobLabel, 'queued')

    const promise = new Promise((resolve, reject) => {
      const job = {
        jobId,
        jobLabel,
        run,
        queuedMessage,
        startedMessage,
        successMessage,
        errorMessage,
        onSuccess,
        onError,
        onFinally,
        resolve,
        reject,
      }

      this.queue.push(job)

      const pending = this.queue.length
      Info.show(queuedMessage || `${jobLabel} encolada. Pendientes: ${pending}`)
      this.notify()
      this.processQueue()
    })

    return promise
  }

  async processQueue() {
    if (this.running >= this.concurrentLimit || this.queue.length === 0) {
      return
    }

    const job = this.queue.shift()
    this.running += 1
    const trackedJob = this.jobs.find((x) => x.id === job.jobId)
    if (trackedJob) {
      trackedJob.status = 'running'
      trackedJob.startedAt = Date.now()
    }
    this.notify()

    if (job.startedMessage) {
      Info.show(job.startedMessage)
    }

    try {
      const result = await job.run()

      if (typeof job.onSuccess === 'function') {
        job.onSuccess(result)
      }

      if (trackedJob) {
        trackedJob.status = 'success'
        trackedJob.finishedAt = Date.now()
      }

      if (job.successMessage) {
        Success.show(job.successMessage)
      }

      job.resolve(result)
    } catch (error) {
      if (typeof job.onError === 'function') {
        job.onError(error)
      }

      if (trackedJob) {
        trackedJob.status = 'error'
        trackedJob.finishedAt = Date.now()
        trackedJob.errorMessage =
          error?.response?.data?.error || error?.message || 'Error en tarea en segundo plano'
      }

      if (job.errorMessage) {
        ErrorToast.show(job.errorMessage)
      } else if (error?.response?.data?.error) {
        ErrorToast.show(error.response.data.error)
      } else if (error?.message) {
        ErrorToast.show(error.message)
      } else {
        ErrorToast.show('Error en tarea en segundo plano')
      }

      job.reject(error)
    } finally {
      if (typeof job.onFinally === 'function') {
        job.onFinally()
      }

      this.running -= 1
      this.notify()

      if (this.queue.length > 0) {
        Warning.show(`Quedan ${this.queue.length} tarea(s) en cola`)
      }

      this.processQueue()
    }
  }
}

export const backgroundTaskQueue = new BackgroundTaskQueue(1)
export default BackgroundTaskQueue
